import { graphql } from 'gatsby';
import React, { Fragment, memo, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { styleMods } from '../utils';
import { cmpByKey, cmpReverse } from '../utils/cmp';
import Columns, { Column } from './columns';
import HidableBox from './hidable-box';
import ImageCarousel from './image-carousel';
import IntlString from './intl-string';
import IntlStringHtml from './intl-string-html';
import style from './map-artist.module.scss';
import RatioImage from './ratio-image';

/**
 * @param {{children: React.ReactNode, fullWidth?: boolean}} props
*/
function ArtistBlock({children, fullWidth}) {
	return (<div className={styleMods(style, 'block', fullWidth && 'fullWidth')}>{children}</div>);
}

export const query = graphql`
	fragment MapArtist on ArtistsJson {
		slug
		name {
			...IntlString
		}
		projectNumber
		projectName {
			...IntlString
		}
		projectAudioDescription {
			local {
				publicURL
			}
			mimetype
		}
		locationImage {
			...RatioImage
		}
		location {
			...IntlString
		}
		gender
		aboutArtistsWork {
			...IntlStringHtml
		}
		aboutArtist {
			...IntlStringHtml
		}
		otherProjects {
			year
			text {
				...IntlString
			}
		}
		projectImages {
			...ImageCarousel
		}
	}
`

export default memo(
	/**
	 * @param {{
	 *    open: boolean,
	 *    toggleOpen: () => void,
	 *    artist: import("../../tmp/graphql-types").MapArtistFragment,
	 *    selectMapTarget: () => void,
	 *  }} props
	 **/
	function MapArtists({
		open,
		toggleOpen,
		artist,
		selectMapTarget,
	}) {
		/** @type {React.MutableRefObject<HTMLSpanElement | null>} */
		const anchorRef = useRef(null);

		useEffect(
			() => {
				setTimeout(
					() => {
						if (open && anchorRef.current) {
							anchorRef.current.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
								inline: 'nearest',
							});
						}
					},
					500,
				);
			},
			[open],
		);

		const otherProjects2 = useMemo(
			() => {
				return artist.otherProjects
					.sort(cmpReverse(cmpByKey(({year}) => year)))
					.map(({year, text}, i) => (
						<div key={i} className={style.otherProjectsYearGroup}>
							<dt className={style.otherProjectsYear}>{year}</dt>

							<dd className={style.otherProjectsText}>
								<IntlString handleLines {...text} />
							</dd>
						</div>
					));
			},
			[artist.otherProjects],
		);

		return (
			<article className={style.base}>
				<span
					id={artist.slug}
					className={style.anchor}
					ref={anchorRef}
				/>

				<HidableBox
					visible={open}
					toggleVisible={toggleOpen}
					header={(
						<div className={style.header}>
							<div className={style.nameProjectName}>
								<h1 className={style.name}>
									<IntlString {...artist.name} />
								</h1>

								<h2 className={style.projectName}>
									<IntlString {...artist.projectName} />
								</h2>
							</div>

							<h2 className={style.number}>
								{artist.projectNumber}
							</h2>
						</div>
					)}
				>
					<div className={style.body}>
						<ArtistBlock fullWidth>
							<ImageCarousel images={artist.projectImages} />
						</ArtistBlock>

						<ArtistBlock>
							<h3 className={style.bodyHeadline}>
								<FormattedMessage
									id="artist.aboutArtistsWork"
									values={{gender: artist.gender}}
								/>
							</h3>

							<Columns
								columns={[
									new Column({width: 5}, (
										<div className={style.aboutArtistsWork}>
											<IntlStringHtml {...artist.aboutArtistsWork} />
										</div>
									)),
									new Column({width: 2},
										artist.locationImage && (
											<div
												role="button"
												tabIndex={0}
												onClick={selectMapTarget}
												className={style.locationLink}
											>
												<RatioImage
													{...artist.locationImage}
													title={artist.location}
												/>
											</div>
										)
									),
								]}
							/>

							{open && artist.projectAudioDescription?.local?.publicURL && (
								<audio
									controls
									preload="metadata"
									className={style.projectAudioDescription}
								>
									<source
										src={artist.projectAudioDescription.local.publicURL}
										type={artist.projectAudioDescription.mimetype}
									/>
								</audio>
							)}
						</ArtistBlock>

						<ArtistBlock>
							<Columns
								columns={[
									new Column({width: 2}, (
										<Fragment>
											<h3 className={style.bodyHeadline}>
												<FormattedMessage
													id="artist.aboutArtist"
													values={{gender: artist.gender}}
												/>
											</h3>
											<div className={style.aboutArtist}>
												<IntlStringHtml {...artist.aboutArtist} />
											</div>
										</Fragment>
									)),
									new Column({width: 5},
										0 < otherProjects2.length && (
											<Fragment>
												<h3 className={style.bodyHeadline}>
													<FormattedMessage
														id="artist.expositions"
														values={{gender: artist.gender}}
													/>
												</h3>

												<dl className={style.otherProjectsList}>
													{otherProjects2}
												</dl>
											</Fragment>
										)
									),
								]}
							/>
						</ArtistBlock>
					</div>
				</HidableBox>
			</article>
		);
	}
);
